import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useLoader from '../../../hooks/useLoader';
import { ResponseList } from '../../../models/Response';
import { getMonthStartAndEnd } from '../../../modules/Common';
import { MasterCompany } from '../../../models/company/MasterCompany';
import { useAdminCompanies } from '../../../hooks';
import { MultiValue } from 'react-select';

export type WorkflowUsageRate = {
    datex: string;
    countInstance: string;
    countCallback: string;
};
export type WorkflowUsageRateDetail = {
    name: string;
    wfType: string;
} & WorkflowUsageRate;

type SearchInput = {
    sid: string;
    sdate: string;
    edate: string;
};

// const now = new Date();
// const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
// const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);  // 0 means the last day of the previous month
const { start: firstOfMonth, end: lastOfMonth } = getMonthStartAndEnd(
    new Date()
);

interface OptionValue {
    value: string;
    label: string;
}

export default function WorkflowUsageRateViewModel() {
    const loader = useLoader();

    const { adminCompanies, isAllowedAllCompanies } = useAdminCompanies();
    const adminCompanyOptions = useMemo(() => {
        if (!adminCompanies) {
            return undefined;
        }

        const options: MasterCompany[] = [
            {
                sid: 'all',
                company_name: 'เลือกทั้งหมด'
            } as MasterCompany
        ];

        return options.concat(adminCompanies);
    }, [adminCompanies, isAllowedAllCompanies]);

    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [fetchingCount, setFetchingCount] = useState(0);
    const [selectedCompany, setSelectedCompany] = useState<MasterCompany[]>([]);
    const [workflowUsageRate, setWorkflowUsageRate] =
        useState<WorkflowUsageRate[]>();
    const [workflowUsageRateDetail, setWorkflowUsageRateDetail] =
        useState<WorkflowUsageRateDetail[]>();
    const [searchInputs, setSearchInput] = useState<SearchInput>({
        sid: '',
        sdate: firstOfMonth,
        edate: lastOfMonth
    });

    const isRunSetterOnce = useRef(false);
    useEffect(() => {
        if (
            isRunSetterOnce.current ||
            !adminCompanies ||
            adminCompanies.length === 0
        ) {
            return;
        }

        setSelectedCompany(adminCompanies);
        setSearchInput(prev => ({
            ...prev,
            sid: adminCompanies?.map(company => company.sid).join(',') || ''
        }));

        fetchWorkflowUsageRateDetail({
            sid: adminCompanies?.map(company => company.sid).join(',') || '',
            sdate: firstOfMonth,
            edate: lastOfMonth
        });

        isRunSetterOnce.current = true;
    }, [adminCompanies]);

    // handle loader display
    useEffect(() => {
        if (fetchingCount > 0) {
            loader.show();
        } else {
            loader.hide();
        }
    }, [fetchingCount]);

    const increaseFetchingCount = () => {
        setFetchingCount(prev => prev + 1);
    };

    const decreaseFetchingCount = () => {
        setFetchingCount(prev => (prev > 0 ? prev - 1 : prev));
    };

    const fetchWorkflowUsageRate = useCallback(async () => {
        increaseFetchingCount();

        try {
            validateDateString(searchInputs.sdate);
            validateDateString(searchInputs.edate);
            const response = await axios.get<ResponseList<WorkflowUsageRate>>(
                '/v1/workflow-usage-rate',
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'WF_APPLICATION'
                        )}`
                    },
                    params: searchInputs
                }
            );

            // console.log('usageRate', response.data.result_list);

            setWorkflowUsageRate(response.data.result_list);
        } catch (error) {
            handleError(error);
        } finally {
            decreaseFetchingCount();
        }
    }, [searchInputs]);

    const fetchWorkflowUsageRateDetail = useCallback(
        async (searchInputs: SearchInput) => {
            increaseFetchingCount();

            try {
                validateDateString(searchInputs.sdate);
                validateDateString(searchInputs.edate);
                const response = await axios.get<
                    ResponseList<WorkflowUsageRateDetail>
                >('/v1/workflow-usage-rate-detail', {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'WF_APPLICATION'
                        )}`
                    },
                    params: searchInputs
                });

                // console.log('usageRateDetail', response.data.result_list);

                setWorkflowUsageRateDetail(response.data.result_list);
            } catch (error) {
                handleError(error);
            } finally {
                decreaseFetchingCount();
            }
        },
        [searchInputs]
    );

    const handleSelectCompany = useCallback(
        (
            selectedOptions: MultiValue<MasterCompany>,
            { action, option, removedValue }
        ) => {
            // console.log('selectoption', selectedOptions)
            if (action === 'remove-value' && removedValue?.isFixed) {
                return;
            }

            if (
                action === 'select-option' &&
                option?.company_name === 'เลือกทั้งหมด'
            ) {
                setSelectedCompany(adminCompanies as MasterCompany[]);
                setSearchInput(prev => ({
                    ...prev,
                    sid:
                        adminCompanies?.map(company => company.sid).join(',') ||
                        ''
                }));
                return;
            }

            setSelectedCompany(selectedOptions as MasterCompany[]);

            const sid =
                selectedOptions?.map(company => company.sid).join(',') || '';

            setSearchInput(prev => ({
                ...prev,
                sid: sid
            }));
        },
        [selectedCompany, adminCompanies]
    );

    const handleSearchInputChange = useCallback(
        (target: keyof SearchInput, value: string) => {
            // console.log('target', target)
            // console.log('value', value)
            setSearchInput(prev => ({
                ...prev,
                [target]: value
            }));
        },
        [searchInputs]
    );

    const handleSearch = useCallback(() => {
        console.log('searchinput', searchInputs);
        console.log('selectedcompany', selectedCompany);
        fetchWorkflowUsageRate();
        fetchWorkflowUsageRateDetail(searchInputs);
    }, [searchInputs]);

    const validateDateString = (dateStr: string) => {
        const dateRegex = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;
        if (
            !dateStr ||
            dateStr.trim().length !== 8 ||
            !dateRegex.test(dateStr)
        ) {
            throw new Error('กรุณาระบุวันที่ต้องการค้นหาให้ถูกต้อง');
        }

        const year = parseInt(dateStr.substring(0, 4), 10);
        const month = parseInt(dateStr.substring(4, 6), 10) - 1; // 0-indexed
        const day = parseInt(dateStr.substring(6, 8), 10);
        const date = new Date(year, month, day);

        if (
            date.getFullYear() !== year ||
            date.getMonth() !== month ||
            date.getDate() !== day
        ) {
            throw new Error('กรุณาระบุวันที่ต้องการค้นหาให้ถูกต้อง');
        }
    };

    const handleError = (error: any) => {
        if (!error) return;

        if (error instanceof AxiosError) {
            Swal.fire('Error', error.response?.data.message || 'ไม่ทราบสาเหตุ');
        } else if (error instanceof Error) {
            Swal.fire('Error', error.message);
        }
    };

    const ExportExcel = useCallback(() => {
        try {
            increaseFetchingCount();

            var date = new Date();
            var filename =
                'WorkflowUsageRate_' + date.getTime().toString() + '.xlsx';
            var datalist: any = [];

            workflowUsageRateDetail?.map(item => {
                let data = {};
                data['datex'] = item.datex;
                data['name'] = item.name;
                data['wftype'] = item.wfType;
                data['countinstance'] = item.countInstance;
                data['countcallback'] = item.countCallback;
                datalist.push(data);
            });

            var workSheet = XLSX.utils.json_to_sheet(datalist);
            var workBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
                workBook,
                workSheet,
                'Workflow Usage Rate'
            );
            XLSX.writeFile(workBook, filename);
        } catch (e) {
            handleError(e);
        } finally {
            decreaseFetchingCount();
        }
    }, [workflowUsageRateDetail]);

    return {
        selectedTab,
        setSelectedTab,
        searchInputs,
        adminCompanyOptions,
        selectedCompany,
        handleSelectCompany,
        workflowUsageRate,
        workflowUsageRateDetail,
        ExportExcel,
        handleSearch,
        handleSearchInputChange
    };
}
